import type { Group } from '@/api'
import type { GroupWithUserData } from '@/hooks/hierarchised-groups'
import { densityPx } from '@/theme/base/density'

export const AGENCY_ICON_PX = densityPx(20)
export const ACTION_ICON_PX = densityPx(15)
export const TITLE_INFO_ICON_PX = densityPx(18)
export const AGENCY_CARD_WIDTH = densityPx(340)
export const AGENCY_CARD_HEIGHT = densityPx(210)

/** List view: base left padding on the agency name column (theme spacing units). */
export const AGENCY_LIST_NAME_BASE_PADDING = 2.5
/** List view: extra left padding per tree depth level (theme spacing units). */
export const AGENCY_LIST_DEPTH_INDENT = 2.5

export function getAgencyListNamePadding(depth: number): number {
  return AGENCY_LIST_NAME_BASE_PADDING + depth * AGENCY_LIST_DEPTH_INDENT
}

export function getAgencyListNestedPadding(depth: number): number {
  return depth * AGENCY_LIST_DEPTH_INDENT
}

export type ViewMode = 'grid' | 'list'

/** Child groups under the organization root (excludes the org-level group). */
export function isAgencyGroup(group: Pick<Group, 'parent'>): boolean {
  return Boolean(group.parent)
}

export function filterAgencyGroups(groups: Group[]): Group[] {
  return groups.filter(isAgencyGroup)
}

export function countAgencyGroups(groups: Group[]): number {
  return filterAgencyGroups(groups).length
}

/** Extension point when API exposes an admin display name on Group. */
export type GroupWithAdministrator = Group & { administratorName?: string }

export function getAgencyAdministratorLine(
  group: Group,
  t: (k: string) => string
): { text: string; assigned: boolean } {
  const raw = (group as GroupWithAdministrator).administratorName?.trim()
  if (raw) return { text: raw, assigned: true }
  return { text: t('agenciesAdministratorUnassigned'), assigned: false }
}

export function collectMemberCountsByGid(
  nodes: GroupWithUserData[],
  acc: Map<string, number>
): void {
  for (const node of nodes) {
    if (node.gid) {
      acc.set(node.gid, node.membersDetails?.length ?? 0)
    }
    if (node.children?.length) {
      collectMemberCountsByGid(node.children, acc)
    }
  }
}

export function isNestedAgency(
  group: Pick<Group, 'parent'>,
  mainGroupGid: string | undefined,
  groupByGid: Map<string, Group>
): boolean {
  if (!group.parent || !mainGroupGid || group.parent === mainGroupGid) {
    return false
  }
  return groupByGid.has(group.parent)
}

export function getAgencyParentName(
  group: Pick<Group, 'parent'>,
  groupByGid: Map<string, Group>
): string | undefined {
  if (!group.parent) return undefined
  return groupByGid.get(group.parent)?.name
}

function collectDirectChildrenOfParent(
  nodes: GroupWithUserData[],
  parentGid: string,
  acc: GroupWithUserData[]
): void {
  for (const node of nodes) {
    if (node.parent === parentGid) {
      acc.push(node)
    }
    if (node.children?.length) {
      collectDirectChildrenOfParent(node.children, parentGid, acc)
    }
  }
}

/** Groups whose `parent` equals `org.mainGroup.gid` (direct agencies of the organization). */
export function getAgencyTreeRoots(
  hierarchisedGroups: GroupWithUserData[],
  mainGroupGid: string | undefined
): GroupWithUserData[] {
  if (!mainGroupGid) return []

  const roots: GroupWithUserData[] = []
  collectDirectChildrenOfParent(hierarchisedGroups, mainGroupGid, roots)
  return roots
}

export type FilterAgencyTreeResult = {
  nodes: GroupWithUserData[]
  expandedGids: Set<string>
}

function matchesAgencyQuery(name: string | undefined, query: string): boolean {
  return Boolean(name?.toLowerCase().includes(query.toLowerCase()))
}

function filterAgencyTreeNode(
  node: GroupWithUserData,
  query: string,
  expandedGids: Set<string>
): GroupWithUserData | null {
  const selfMatches = matchesAgencyQuery(node.name, query)
  const filteredChildren = (node.children ?? [])
    .map((child) => filterAgencyTreeNode(child, query, expandedGids))
    .filter((child): child is GroupWithUserData => child !== null)

  if (!selfMatches && filteredChildren.length === 0) {
    return null
  }

  if (filteredChildren.length > 0 && node.gid) {
    expandedGids.add(node.gid)
  }

  return {
    ...node,
    children: filteredChildren,
  }
}

/** Keeps branches that contain a name match; returns ancestor gids to auto-expand. */
export function filterAgencyTree(
  nodes: GroupWithUserData[],
  query: string
): FilterAgencyTreeResult {
  const trimmed = query.trim()
  if (!trimmed) {
    return { nodes, expandedGids: new Set<string>() }
  }

  const expandedGids = new Set<string>()
  const filteredNodes = nodes
    .map((node) => filterAgencyTreeNode(node, trimmed, expandedGids))
    .filter((node): node is GroupWithUserData => node !== null)

  return { nodes: filteredNodes, expandedGids }
}

/** Maps a hierarchy node to a flat Group (prefers the API map entry). */
export function toAgencyGroup(
  node: GroupWithUserData,
  groupByGid: Map<string, Group>
): Group {
  const fromMap = groupByGid.get(node.gid)
  if (fromMap) return fromMap

  return {
    gid: node.gid,
    name: node.name,
    parent: node.parent,
    organization: node.organization,
    type: node.type,
  }
}

/** Depth-first flattening: each parent is immediately followed by its children. */
export function flattenAgencyTree(
  nodes: GroupWithUserData[]
): GroupWithUserData[] {
  const result: GroupWithUserData[] = []

  const visit = (node: GroupWithUserData) => {
    result.push(node)
    for (const child of node.children ?? []) {
      visit(child)
    }
  }

  for (const node of nodes) {
    visit(node)
  }

  return result
}
